import { z } from 'zod';

/**
 * Form validation utilities and schemas
 */

// Form state management types
export interface FormState<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
  isValid?: boolean;
  isDirty?: boolean;
  submitError?: string | null;
}

export interface FormFieldOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Common validation schemas for form fields
 */
export const ValidationSchemas = {
  // Required text field with custom field name
  requiredText: (fieldName: string) =>
    z
      .string()
      .min(1, `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`),

  // Email validation
  email: z.string().email('Please enter a valid email address'),

  // URL validation
  url: z.string().url('Please enter a valid URL'),

  // Phone number (basic validation)
  phone: z
    .string()
    .regex(/^[\d\s\-\+\(\)]+$/, 'Please enter a valid phone number')
    .min(10, 'Phone number must be at least 10 digits'),

  // Slug (URL-friendly string)
  slug: z
    .string()
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'Must be lowercase letters, numbers, and hyphens only',
    )
    .min(3, 'Slug must be at least 3 characters'),

  // Group name validation
  groupName: z
    .string()
    .min(3, 'Group name must be at least 3 characters')
    .max(100, 'Group name must be 100 characters or less'),

  // Group slug validation
  groupSlug: z
    .string()
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'Must be lowercase letters, numbers, and hyphens only',
    )
    .min(3, 'Slug must be at least 3 characters')
    .max(50, 'Slug must be 50 characters or less'),

  // Description with character limits
  description: (maxChars = 500) =>
    z
      .string()
      .max(maxChars, `Description must be ${maxChars} characters or less`)
      .optional(),

  // Password with minimum length
  password: (minLength = 8) =>
    z.string().min(minLength, `Password must be at least ${minLength} characters`),

  // Meeting-specific validations
  meetingTitle: z
    .string()
    .min(3, 'Meeting title must be at least 3 characters')
    .max(120, 'Meeting title must be 120 characters or less'),

  meetingDescription: z
    .string()
    .min(10, 'Meeting description must be at least 10 characters')
    .max(2000, 'Meeting description must be 2000 characters or less')
    .optional(),

  // Broadcast message validation
  broadcastMessage: z
    .string()
    .min(1, 'Broadcast message is required')
    .max(500, 'Broadcast message must be 500 characters or less'),

  // DateTime validation (ISO 8601 datetime string)
  datetime: z
    .string()
    .datetime({ message: 'Please enter a valid date and time' })
    .or(z.string().min(1, 'Date and time is required')),

  // Capacity validation
  capacity: z
    .number()
    .int('Capacity must be a whole number')
    .positive('Capacity must be greater than 0')
    .max(10000, 'Capacity must be 10,000 or less')
    .optional(),
};

export interface ValidationResult<T> {
  errors: Partial<Record<keyof T, string>>;
  isValid: boolean;
}

/**
 * Validate form values against a Zod schema
 */
export function validateFormValues<T>(
  values: Partial<T>,
  schema: z.ZodSchema<T>,
): ValidationResult<T> {
  try {
    schema.parse(values);
    return { errors: {}, isValid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Partial<Record<keyof T, string>> = {};
      error.errors.forEach(err => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof T;
          errors[key] = err.message;
        }
      });
      return { errors, isValid: false };
    }
    return { errors: {}, isValid: false };
  }
}

/**
 * Create initial form state with default values
 */
export function createInitialFormState<T>(values: T): FormState<T> {
  return {
    values,
    errors: {},
    touched: {},
    isSubmitting: false,
    isValid: false,
    isDirty: false,
  };
}

/**
 * Update a single field in form state
 */
export function updateFormField<T>(
  state: FormState<T>,
  field: keyof T,
  value: T[keyof T],
): FormState<T> {
  return {
    ...state,
    values: {
      ...state.values,
      [field]: value,
    },
    touched: {
      ...state.touched,
      [field]: true,
    },
  };
}

/**
 * Set form errors
 */
export function setFormErrors<T>(
  state: FormState<T>,
  errors: Partial<Record<keyof T, string>>,
): FormState<T> {
  return {
    ...state,
    errors,
  };
}

/**
 * Set form submitting state
 */
export function setFormSubmitting<T>(
  state: FormState<T>,
  isSubmitting: boolean,
): FormState<T> {
  return {
    ...state,
    isSubmitting,
  };
}

/**
 * Reset form to initial state
 */
export function resetForm<T>(initialValues: T): FormState<T> {
  return createInitialFormState(initialValues);
}
